import React, { useState, useMemo, useRef, useEffect, useId } from 'react';
import { Button, Card, Statistic, Row, Col, Tooltip, Badge } from 'antd';
import { ReloadOutlined, UndoOutlined, InfoCircleOutlined } from '@ant-design/icons';

/* -----------------------------
   Self-intersection helpers
   ----------------------------- */

const EPS = 1e-9;

const orientation = (a, b, c) => {
    // cross((b-a),(c-a))
    const v = (b.y - a.y) * (c.x - a.x) - (b.x - a.x) * (c.y - a.y);
    if (Math.abs(v) < EPS) return 0; // collinear
    return v > 0 ? 1 : 2; // 1/2 are just two different non-zero values
};

const onSegment = (a, b, c) => {
    // b lies on segment ac (assumes collinear)
    return (
        Math.min(a.x, c.x) - EPS <= b.x && b.x <= Math.max(a.x, c.x) + EPS &&
        Math.min(a.y, c.y) - EPS <= b.y && b.y <= Math.max(a.y, c.y) + EPS
    );
};

const segmentsIntersect = (p1, q1, p2, q2) => {
    const o1 = orientation(p1, q1, p2);
    const o2 = orientation(p1, q1, q2);
    const o3 = orientation(p2, q2, p1);
    const o4 = orientation(p2, q2, q1);

    // General case
    if (o1 !== o2 && o3 !== o4) return true;

    // Collinear special cases
    if (o1 === 0 && onSegment(p1, p2, q1)) return true;
    if (o2 === 0 && onSegment(p1, q2, q1)) return true;
    if (o3 === 0 && onSegment(p2, p1, q2)) return true;
    if (o4 === 0 && onSegment(p2, q1, q2)) return true;

    return false;
};

/**
 * Hero Geoboard Component
 * High-fidelity interactive geoboard with rubber-band physics,
 * smart snapping, and real-time geometric calculations.
 */
export const Geoboard = ({ config = {} }) => {
    // 1. Config & Constants
    // Guard against gridSize < 2 to prevent division by zero
    const gridSize = Math.max(2, config.gridSize || 5);
    const boardType = String(config.boardType || 'SQUARE').toUpperCase();
    const showCalc = config.showCalculations ?? true;

    // --- DATA ADAPTER START ---
    // Normalize initialPath to always be an array of peg IDs that exist in the current board:
    //   - SQUARE: "col-row"
    //   - CIRCULAR: "c-<0..11>" or "center"
    const rawInitialPath = config.initialPath || [];
    const normalizedInitialPath = useMemo(() => {
        const normalizeString = (s) => {
            if (s == null) return null;
            const t = String(s).trim();
            if (!t) return null;

            if (boardType === 'CIRCULAR') {
                if (t.toLowerCase() === 'center') return 'center';
                if (/^c-\d+$/i.test(t)) return t.toLowerCase();

                // allow legacy forms like "3" or "3-0"
                const m = t.match(/^(\d+)(?:-\d+)?$/);
                if (m) return `c-${Number(m[1])}`;
                return null;
            }

            // SQUARE
            if (/^\d+-\d+$/.test(t)) return t;
            const m2 = t.match(/^(\d+)\s*,\s*(\d+)$/);
            if (m2) return `${m2[1]}-${m2[2]}`;
            return null;
        };

        const normalizeObject = (obj) => {
            if (!obj || typeof obj !== 'object') return null;

            // allow { id: "..." } / { pegId: "..." }
            if (typeof obj.id === 'string') return normalizeString(obj.id);
            if (typeof obj.pegId === 'string') return normalizeString(obj.pegId);

            // canonical backend schema: { col: number, row: number }
            const col = obj.col;
            const row = obj.row;

            if (boardType === 'CIRCULAR') {
                // backend contract: use col as index 0..11; row ignored
                const idx = Number(col);
                if (!Number.isFinite(idx)) return null;
                if (idx < 0 || idx > 11) return null;
                return `c-${idx}`;
            }

            // SQUARE
            const c = Number(col);
            const r = Number(row);
            if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
            return `${c}-${r}`;
        };

        return rawInitialPath
            .map((item) => {
                if (typeof item === 'string') return normalizeString(item);
                if (typeof item === 'object' && item !== null) return normalizeObject(item);
                return null;
            })
            .filter(Boolean);
    }, [rawInitialPath, boardType]);
    // --- DATA ADAPTER END ---

    // Unique ID for SVG patterns to prevent conflicts if multiple boards exist
    const gridPatternId = useId();

    // Canvas Dimensions
    const WIDTH = 400;
    const HEIGHT = 400;
    const PADDING = 40;

    // 2. Geometry Engine (Memoized for Performance)
    const { pegs, pegMap, unitScale, gridSpacing } = useMemo(() => {
        const generatedPegs = [];
        let spacing = 1;

        if (boardType === 'CIRCULAR') {
            const cx = WIDTH / 2;
            const cy = HEIGHT / 2;
            const radius = Math.min(WIDTH, HEIGHT) / 2 - PADDING;
            const points = 12; // 12-hour clock style

            // Define Scale: Radius = 1 Unit
            spacing = radius;

            // Center Peg
            generatedPegs.push({ id: 'center', x: cx, y: cy, label: 'C' });

            // Outer Pegs
            for (let i = 0; i < points; i++) {
                const angle = (i * 30 - 90) * (Math.PI / 180);
                generatedPegs.push({
                    id: `c-${i}`,
                    x: cx + radius * Math.cos(angle),
                    y: cy + radius * Math.sin(angle),
                    label: String(i === 0 ? 12 : i)
                });
            }
        } else {
            // SQUARE Grid
            // Scale: 1 Unit = Distance between two adjacent pegs
            spacing = (WIDTH - PADDING * 2) / (gridSize - 1);

            for (let r = 0; r < gridSize; r++) {
                for (let c = 0; c < gridSize; c++) {
                    generatedPegs.push({
                        id: `${c}-${r}`,
                        x: PADDING + c * spacing,
                        y: PADDING + r * spacing,
                        col: c,
                        row: r
                    });
                }
            }
        }

        const map = new Map(generatedPegs.map((p) => [p.id, p]));
        return { pegs: generatedPegs, pegMap: map, unitScale: spacing, gridSpacing: spacing };
    }, [gridSize, boardType]);

    // 3. State Management
    const [path, setPath] = useState([]);
    const [isClosed, setIsClosed] = useState(false);

    // Performance: O(1) lookup for currently selected pegs
    const pathSet = useMemo(() => new Set(path), [path]);

    // Mouse Interaction State
    const [mousePos, setMousePos] = useState(null);
    const [hoverPeg, setHoverPeg] = useState(null);
    const svgRef = useRef(null);

    // Load Initial Configuration (Normalized)
    useEffect(() => {
        if (normalizedInitialPath && normalizedInitialPath.length > 0) {
            // Filter out invalid IDs that don't exist on this specific board size/type
            const validPath = normalizedInitialPath.filter((id) => pegMap.has(id));

            if (validPath.length > 0) {
                setPath(validPath);

                // Auto-close if the last point equals the first
                const closed = validPath.length > 2 && validPath[0] === validPath[validPath.length - 1];
                setIsClosed(closed);
                return;
            }
        }

        // If nothing valid provided, ensure clean closed-state
        setIsClosed(false);
    }, [normalizedInitialPath, pegMap]);

    // Self-intersection detection (warn students: shoelace area is ambiguous for complex polygons)
    const hasSelfIntersection = useMemo(() => {
        // Need at least 4 points to have a crossing in a polyline
        if (path.length < 4) return false;

        // Build segment list from the visible rubber band.
        // If closed, include the closing edge (if not already included).
        const ids = isClosed
            ? (path[0] === path[path.length - 1] ? path : [...path, path[0]])
            : path;

        const pts = ids.map((id) => pegMap.get(id)).filter(Boolean);
        if (pts.length < 4) return false;

        const segCount = pts.length - 1;

        for (let i = 0; i < segCount; i++) {
            const a1 = pts[i];
            const a2 = pts[i + 1];

            for (let j = i + 1; j < segCount; j++) {
                // Skip adjacent segments (share a vertex)
                if (Math.abs(i - j) <= 1) continue;

                // If closed, also skip the first/last adjacency
                if (isClosed && i === 0 && j === segCount - 1) continue;

                const b1 = pts[j];
                const b2 = pts[j + 1];

                if (segmentsIntersect(a1, a2, b1, b2)) return true;
            }
        }

        return false;
    }, [path, isClosed, pegMap]);

    // 4. Mathematical Core (Shoelace & Distance)
    const stats = useMemo(() => {
        if (path.length < 2) return { area: 0, perimeter: 0 };

        // Perimeter: measure drawn segments only (and include the closing segment if closed)
        const idsForPerimeter = isClosed
            ? (path[0] === path[path.length - 1] ? path : [...path, path[0]])
            : path;

        let perimeterPx = 0;
        for (let i = 0; i < idsForPerimeter.length - 1; i++) {
            const p1 = pegMap.get(idsForPerimeter[i]);
            const p2 = pegMap.get(idsForPerimeter[i + 1]);
            if (!p1 || !p2) continue;
            perimeterPx += Math.hypot(p2.x - p1.x, p2.y - p1.y);
        }

        // Area: only when the shape is closed, has >= 3 vertices, AND is not self-intersecting
        let areaPx = 0;
        if (isClosed && !hasSelfIntersection) {
            const idsForArea = (path[0] === path[path.length - 1]) ? path : [...path, path[0]];
            // Need at least 4 ids including closure: A,B,C,A
            if (idsForArea.length >= 4) {
                for (let i = 0; i < idsForArea.length - 1; i++) {
                    const p1 = pegMap.get(idsForArea[i]);
                    const p2 = pegMap.get(idsForArea[i + 1]);
                    if (!p1 || !p2) continue;
                    areaPx += (p1.x * p2.y) - (p2.x * p1.y);
                }
            }
        }

        const areaUnits = (isClosed && !hasSelfIntersection) ? (Math.abs(areaPx) / 2) / (unitScale * unitScale) : 0;
        const perimeterUnits = perimeterPx / unitScale;

        return {
            area: Number(areaUnits.toFixed(2)),
            perimeter: Number(perimeterUnits.toFixed(2))
        };
    }, [path, isClosed, pegMap, unitScale, hasSelfIntersection]);

    // 5. Interaction Handlers
    const handlePegClick = (pegId) => {
        if (isClosed) return;

        if (path.length === 0) {
            setPath([pegId]);
            return;
        }

        // Check for Closure
        if (path.length >= 2 && pegId === path[0]) {
            setPath((prev) => [...prev, pegId]);
            setIsClosed(true);
            setMousePos(null);
            return;
        }

        // Prevent duplicates (back-to-back same peg)
        if (path[path.length - 1] === pegId) return;

        setPath((prev) => [...prev, pegId]);
    };

    const handleMouseMove = (e) => {
        if (isClosed || path.length === 0 || !svgRef.current) return;

        const rect = svgRef.current.getBoundingClientRect();
        const rawX = e.clientX - rect.left;
        const rawY = e.clientY - rect.top;

        // Smart Snapping
        let closest = null;
        let minDist = 30;

        for (const peg of pegs) {
            const dist = Math.hypot(peg.x - rawX, peg.y - rawY);
            if (dist < minDist) {
                closest = peg;
                minDist = dist;
            }
        }

        if (closest) {
            setHoverPeg(closest.id);
            setMousePos({ x: closest.x, y: closest.y });
        } else {
            setHoverPeg(null);
            setMousePos({ x: rawX, y: rawY });
        }
    };

    const reset = () => {
        setPath([]);
        setIsClosed(false);
        setHoverPeg(null);
        setMousePos(null);
    };

    const undo = () => {
        if (isClosed) {
            setIsClosed(false);
            setPath((prev) => prev.slice(0, -1));
        } else {
            setPath((prev) => {
                const next = prev.slice(0, -1);
                if (next.length === 0) {
                    setMousePos(null);
                    setHoverPeg(null);
                }
                return next;
            });
        }
    };

    // 6. Safe Visual Helpers
    const getPathString = (pointIds) => {
        const pts = pointIds.map((id) => pegMap.get(id)).filter(Boolean);
        if (pts.length === 0) return '';
        return `M ${pts.map((p) => `${p.x} ${p.y}`).join(' L ')}`;
    };

    const areaDisplayValue = hasSelfIntersection ? '-' : stats.area;

    return (
        <div className="flexColumn gap-medium width-100">
            {/* A. VISUAL STAGE */}
            <div
                className="ch-emph-block flexJustifyCentre padding--m relative"
                style={{
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                }}
            >
                <div className="absolute top-3 right-3 z-10">
                    <Badge
                        status={isClosed ? 'success' : 'processing'}
                        text={isClosed ? <span className="text-success font-bold">Closed Shape</span> : 'Drawing...'}
                    />
                </div>

                <svg
                    ref={svgRef}
                    width={WIDTH}
                    height={HEIGHT}
                    style={{ overflow: 'visible', cursor: isClosed ? 'default' : 'crosshair', touchAction: 'none' }}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => { setMousePos(null); setHoverPeg(null); }}
                >
                    {/* Unique Pattern ID prevents collisions */}
                    {boardType === 'SQUARE' && (
                        <defs>
                            <pattern id={gridPatternId} width={gridSpacing} height={gridSpacing} patternUnits="userSpaceOnUse">
                                <path d={`M ${gridSpacing} 0 L 0 0 0 ${gridSpacing}`} fill="none" stroke="#e2e8f0" strokeWidth="1" />
                            </pattern>
                        </defs>
                    )}
                    {boardType === 'SQUARE' && (
                        <rect
                            x={PADDING}
                            y={PADDING}
                            width={WIDTH - PADDING * 2}
                            height={HEIGHT - PADDING * 2}
                            fill={`url(#${gridPatternId})`}
                        />
                    )}

                    {/* Path Fill */}
                    {isClosed && (
                        <path
                            d={getPathString(path) + " Z"}
                            fill="rgba(59, 130, 246, 0.15)"
                            stroke="none"
                            className="animate-fade-in"
                        />
                    )}

                    {/* Rubber Bands */}
                    <path
                        d={getPathString(path)}
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ filter: 'drop-shadow(0 2px 3px rgba(59, 130, 246, 0.3))' }}
                    />

                    {/* Ghost Line */}
                    {!isClosed && path.length > 0 && mousePos && pegMap.get(path[path.length - 1]) && (
                        <line
                            x1={pegMap.get(path[path.length - 1]).x}
                            y1={pegMap.get(path[path.length - 1]).y}
                            x2={mousePos.x}
                            y2={mousePos.y}
                            stroke="#fbbf24"
                            strokeWidth="3"
                            strokeDasharray="6,4"
                            className="animate-pulse"
                        />
                    )}

                    {/* Pegs */}
                    {pegs.map((peg) => {
                        const isStart = path.length > 0 && path[0] === peg.id;
                        const isSelected = pathSet.has(peg.id);
                        const isHover = hoverPeg === peg.id;
                        const canClose = !isClosed && path.length > 2 && isStart;

                        // Correct Scaling Transform (Center Pivot)
                        const transform = isHover
                            ? `translate(${peg.x}, ${peg.y}) scale(1.2) translate(${-peg.x}, ${-peg.y})`
                            : undefined;

                        return (
                            <g
                                key={peg.id}
                                onClick={() => handlePegClick(peg.id)}
                                style={{ cursor: 'pointer', transition: 'transform 0.1s ease-out' }}
                                transform={transform}
                            >
                                <circle cx={peg.x} cy={peg.y} r={20} fill="transparent" />

                                <circle
                                    cx={peg.x}
                                    cy={peg.y}
                                    r={5}
                                    fill={isSelected ? "#2563eb" : "#94a3b8"}
                                    stroke={canClose ? "#10b981" : (isHover ? "#3b82f6" : "white")}
                                    strokeWidth={canClose || isHover ? 3 : 2}
                                />

                                {(boardType === 'CIRCULAR' || isStart) && (
                                    <text
                                        x={peg.x}
                                        y={peg.y - 12}
                                        textAnchor="middle"
                                        fontSize="10"
                                        fontWeight="bold"
                                        fill="#64748b"
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        {canClose ? "Close" : (isStart ? "Start" : "")}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            {/* B. CONTROLS & STATS */}
            <Card className="ch-card padding--m bg-white border-all--s shadow-sm">
                <Row gutter={24} align="middle">
                    <Col span={showCalc ? 10 : 24}>
                        <div className="flexAlignCentre gap-small">
                            <Tooltip title="Undo last segment">
                                <Button icon={<UndoOutlined />} onClick={undo} disabled={path.length === 0}>
                                    Undo
                                </Button>
                            </Tooltip>
                            <Tooltip title="Clear board">
                                <Button icon={<ReloadOutlined />} onClick={reset} danger disabled={path.length === 0}>
                                    Clear
                                </Button>
                            </Tooltip>

                            <div className="margin-l--m ch-text-small text-muted flexAlignCentre gap-xs">
                                <InfoCircleOutlined />
                                <span>{isClosed ? "Shape complete." : "Click pegs to draw."}</span>
                            </div>
                        </div>
                    </Col>

                    {showCalc && (
                        <Col span={14}>
                            <div className="flexRow justify-end gap-xlarge">
                                <Statistic
                                    title="Perimeter"
                                    value={stats.perimeter}
                                    precision={1}
                                    suffix="units"
                                    valueStyle={{ color: '#3b82f6', fontWeight: 600 }}
                                />
                                <Statistic
                                    title="Area"
                                    value={areaDisplayValue}
                                    precision={1}
                                    suffix={areaDisplayValue === '-' ? '' : 'units²'}
                                    valueStyle={{
                                        color: (isClosed && !hasSelfIntersection) ? '#10b981' : '#94a3b8',
                                        fontWeight: 600
                                    }}
                                />
                            </div>
                        </Col>
                    )}
                </Row>

                {hasSelfIntersection && (
                    <div className="margin-t--s">
                        <Badge
                            status="warning"
                            text="Your rubber band crosses itself — area isn’t defined in the usual way."
                        />
                    </div>
                )}
            </Card>
        </div>
    );
};
